import React from 'react';
import { cn } from '../../lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

// Amazon Design System Card Variants
const cardVariants = cva(
  // Base styles
  'rounded-lg border bg-card text-card-foreground shadow-sm transition-all duration-200',
  {
    variants: {
      variant: {
        // Default card with subtle border
        default: 'border-[var(--border-default)] bg-white hover:shadow-md',
        // Elevated card with more shadow
        elevated: 'border-[var(--border-light)] bg-white shadow-md hover:shadow-lg',
        // Outlined card with Amazon orange accent
        outlined: 'border-[var(--amazon-orange)] bg-white hover:bg-[var(--amazon-orange)]/5',
        // Filled card with background color
        filled: 'border-[var(--border-light)] bg-[var(--background-secondary)] hover:bg-[var(--background-secondary)]/80',
        // Interactive card for clickable items
        interactive: 'border-[var(--border-default)] bg-white hover:border-[var(--amazon-orange)] hover:shadow-md cursor-pointer',
      },
      size: {
        sm: 'p-4',
        default: 'p-6',
        lg: 'p-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const cardHeaderVariants = cva(
  'flex flex-col space-y-1.5',
  {
    variants: {
      size: {
        sm: 'pb-3',
        default: 'pb-4',
        lg: 'pb-6',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const cardTitleVariants = cva(
  'font-[var(--font-weight-semibold)] leading-none tracking-tight',
  {
    variants: {
      size: {
        sm: 'text-[var(--font-size-lg)]',
  default: 'text-[var(--font-size-xl)]',
  lg: 'text-[var(--font-size-2xl)]',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const cardDescriptionVariants = cva(
  'text-[var(--text-secondary)]',
  {
    variants: {
      size: {
        sm: 'text-[var(--font-size-sm)]',
  default: 'text-[var(--font-size-sm)]',
  lg: 'text-[var(--font-size-base)]',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const cardContentVariants = cva(
  '',
  {
    variants: {
      size: {
        sm: 'pt-0',
        default: 'pt-0',
        lg: 'pt-0',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const cardFooterVariants = cva(
  'flex items-center',
  {
    variants: {
      size: {
        sm: 'pt-3',
        default: 'pt-4',
        lg: 'pt-6',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  asChild?: boolean;
}

export interface CardHeaderProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardHeaderVariants> {}

export interface CardTitleProps
  extends React.HTMLAttributes<HTMLHeadingElement>,
    VariantProps<typeof cardTitleVariants> {}

export interface CardDescriptionProps
  extends React.HTMLAttributes<HTMLParagraphElement>,
    VariantProps<typeof cardDescriptionVariants> {}

export interface CardContentProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardContentVariants> {}

export interface CardFooterProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardFooterVariants> {}

const AmazonCard = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, size, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant, size, className }))}
      {...props}
    />
  )
);
AmazonCard.displayName = 'AmazonCard';

const AmazonCardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, size, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardHeaderVariants({ size, className }))}
      {...props}
    />
  )
);
AmazonCardHeader.displayName = 'AmazonCardHeader';

const AmazonCardTitle = React.forwardRef<HTMLParagraphElement, CardTitleProps>(
  ({ className, size, ...props }, ref) => (
    // eslint-disable-next-line jsx-a11y/heading-has-content
    <h3
      ref={ref}
      className={cn(cardTitleVariants({ size, className }))}
      {...props}
    />
  )
);
AmazonCardTitle.displayName = 'AmazonCardTitle';

const AmazonCardDescription = React.forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ className, size, ...props }, ref) => (
    <p
      ref={ref}
      className={cn(cardDescriptionVariants({ size, className }))}
      {...props}
    />
  )
);
AmazonCardDescription.displayName = 'AmazonCardDescription';

const AmazonCardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, size, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardContentVariants({ size, className }))}
      {...props}
    />
  )
);
AmazonCardContent.displayName = 'AmazonCardContent';

const AmazonCardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, size, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardFooterVariants({ size, className }))}
      {...props}
    />
  )
);
AmazonCardFooter.displayName = 'AmazonCardFooter';

export {
  AmazonCard,
  AmazonCardHeader,
  AmazonCardTitle,
  AmazonCardDescription,
  AmazonCardContent,
  AmazonCardFooter,
  cardVariants,
};

// Usage examples:
// <AmazonCard variant="elevated" size="lg">
//   <AmazonCardHeader>
//     <AmazonCardTitle>Card Title</AmazonCardTitle>
//     <AmazonCardDescription>Card description text</AmazonCardDescription>
//   </AmazonCardHeader>
//   <AmazonCardContent>
//     <p>Card content goes here</p>
//   </AmazonCardContent>
//   <AmazonCardFooter>
//     <AmazonButton variant="primary">Action</AmazonButton>
//   </AmazonCardFooter>
// </AmazonCard>